import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './MobileMenu.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MobileMenuLink from './MobileMenuLink';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import convenienceApi from '../../common/api/convenience';
import ProfileModal from '../ProfileModal/ProfileModal';
import logComponent from '../../common/logComponent';

export default function MobileMenu({ links }) {
  const environmentContext = useContext(EnvironmentContext);
  const [exiting, setExiting] = useState(false);
  const [entering, setEntering] = useState(false);
  const [visible, setVisible] = useState(false);
  const [openedByKeyboard, setOpenedByKeyboard] = useState(false);
  const [stagedOrganizationCount, setStagedOrganizationCount] = useState(null);
  const [messageCount, setMessageCount] = useState(false);
  const [reportsEnabled, setReportsEnabled] = useState(environmentContext.reportsEnabled);
  const [reloadInterval, setReloadInterval] = useState(null);
  const [reportReloadInterval, setReportReloadInterval] = useState(null);
  const [lastReload, setLastReload] = useState(null);
  const [visiblePopup, setVisiblePopup] = useState(null);
  const firstTabRef = useRef(null);
  const lastTabRef = useRef(null);
  const openBtnRef = useRef(null);
  const { hasToken, activeUser: user } = environmentContext;
  const navigate = useNavigate();

  if (logComponent('MobileMenu')) { console.debug('MobileMenu rendered'); }

  const setFirstTabRef = (el) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu setFirstTabRef'); }
    firstTabRef.current = el;
  }

  const setLastTabRef = (el) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu setLastTabRef'); }
    lastTabRef.current = el;
  }

  const setOpenBtnRef = (el) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu setOpenBtnRef'); }
    openBtnRef.current = el;
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('MobileMenu')) { console.debug('MobileMenu onMount'); }
    setReloadInterval(setInterval(silentReloadDynamicMenuParts, 30000));
    setReportReloadInterval(setInterval(silentReloadReports, 1000));

    if (!hasToken) return;
    silentReloadDynamicMenuParts();
    return () => {
      clearInterval(reloadInterval);
      clearInterval(reportReloadInterval);
    };

  }, []);

  useEffect(() => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu rendered useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;
    silentReloadDynamicMenuParts();
  }, [environmentContext.hasToken]);

  useEffect(() => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu useEffect [environmentContext.activeUser]'); }
    if (!hasToken) return;
    setMessageCount(false);
    setStagedOrganizationCount(false);
    silentReloadDynamicMenuParts();
  }, [environmentContext.activeUser]);

  useEffect(() => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu useEffect [entering]'); }
    if (!hasToken || !entering) return;
    lockBody();

    if (openedByKeyboard) firstTabRef.current.focus();

    setTimeout(() => {
      setEntering(false);
    });
  }, [entering]);

  const hideVisiblePopup = () => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu hideVisiblePopup'); }
    setVisiblePopup(null);
  }

  const silentReloadReports = () => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu silentReloadReports'); }
    const { reportsEnabled, hasToken } = environmentContext;
    if (!hasToken) return;

    setReportsEnabled(reportsEnabled);
    setLastReload(Date.now());
  }

  const silentReloadDynamicMenuParts = () => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu silentReloadDynamicMenuParts'); }
    const { activeUser: user, hasToken } = environmentContext;
    if (!hasToken) return;

    if (user.groupTypeString === 'Family') {
      getUnreadMessageCount();
    } else if (user.groupTypeString === 'Facility') {
      if (user.hasCorrespondenceViaGroupsOrOverride) {
        getUnreadMessageCount();
      }
    } else if (user.groupTypeString === 'Corporate') {
    } else if (user.groupTypeString === 'Status Solutions') {
      if (user.isAdmin) {
        getStagedOrganizationCount();
      }
    }

  }

  const getUnreadMessageCount = () => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu getUnreadMessageCount'); }
    const { activeUser: user, hasToken } = environmentContext;
    if (!hasToken) return;
    convenienceApi.getUnreadMessageCount(user)
      .then(
        result => {
          setMessageCount(result);
        },
        error => { }
      );
  }

  const getStagedOrganizationCount = () => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu getStagedOrganizationCount'); }
    const { hasToken } = environmentContext;
    if (!hasToken) return;

    convenienceApi.getStagedOrganizationCount()
      .then(
        result => {
          setStagedOrganizationCount(result);
        },
        error => { }
      );
  }

  const handleKeyDown = (e) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu handleKeyDown'); }
    if (e.key === ' ' || e.key === 'Enter') {
      setOpenedByKeyboard(true);
    }

  }
  const showLinks = (e) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu showLinks'); }
    setEntering(true);
    setVisible(true);
  }

  const lockBody = () => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu lockBody'); }
    document.body.style.overflow = "hidden";
  }

  const unlockBody = () => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu unlockBody'); }
    document.body.style.overflow = "visible";
  }

  const selectMenuItem = (linkPath, linkPopup) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu selectMenuItem'); }
    if (linkPopup) {
      setVisiblePopup(linkPopup);

    } else {
      setVisiblePopup(null);
      navigate(linkPath);
    }
    beginMenuExit();
  }

  const beginMenuExit = () => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu beginMenuExit'); }
    setExiting(true);
  }

  const hideLinks = (e) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu hideLinks'); }
    if (e.propertyName === 'left' || e.propertyName === 'opacity') {
      setVisible(false);
      setExiting(false);
      setOpenedByKeyboard(false);

      unlockBody();
    }
  }

  const handleTab = (e) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu handleTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || shiftKey) return;

    e.preventDefault();
    firstTabRef.current.focus();
  }

  const handleShiftTab = (e) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu handleShiftTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || !shiftKey) return;

    e.preventDefault();
    lastTabRef.current.focus();
  }

  const handleCloseButtonKeyDown = (e) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu handleCloseButtonKeyDown'); }
    if (e.key === ' ' || e.key === 'Enter') {
      setTimeout(() => { openBtnRef.current.focus(); });

      if (e.key === ' ') beginMenuExit();
    } else {
      handleShiftTab(e);
    }
  }

  const handleFocus = (e) => {
    if (logComponent('MobileMenu')) { console.debug('MobileMenu handleFocus'); }
    if (e.relatedTarget === firstTabRef.current) {
      // TODO: For fully-functional focus trap, need
      // to focus the tabbable element prior to e.target
    } else {
      firstTabRef.current.focus();
    }
  }

  return (
    <nav className="Mobile-Menu">
      <button
        ref={setOpenBtnRef}
        className="Mobile-Menu__open-btn"
        onClick={showLinks}
        onKeyDown={handleKeyDown}>
        <FontAwesomeIcon className="Mobile-Menu__toggle-icon" icon="bars"></FontAwesomeIcon>
        {
          (stagedOrganizationCount > 0 || messageCount > 0) &&
          <span className="Mobile-Menu__organization-indicator"></span>
        }

      </button>

      {visible &&
        <div className="Mobile-Menu__links-container">
          <div
            className={'Mobile-Menu__links' +
              (exiting ? ' Mobile-Menu__links--exiting' : '') +
              (entering ? ' Mobile-Menu__links--entering' : '')}
            aria-label="Main navigation"
            onTransitionEnd={exiting ? hideLinks : null} >
            <h2 className="Mobile-Menu__header">
              <button
                className="Mobile-Menu__close-button"
                ref={setFirstTabRef}
                onKeyDown={handleCloseButtonKeyDown}
                onClick={beginMenuExit} >
                <FontAwesomeIcon
                  icon="times"
                  className="Mobile-Menu__close-icon" />
              </button>
            </h2>
            <ul className="Mobile-Menu__links">
              {
                links.map(link =>
                  <MobileMenuLink
                    key={link.linkText}
                    level={1}
                    permittedUsers={link.permittedUsers}
                    user={user}
                    linkAncestors={[]}
                    linkIcon={link.linkIcon}
                    linkText={link.linkText}
                    linkPath={link.linkPath}
                    linkPopup={link.linkPopup}
                    subLinks={link.subLinks}
                    onSelect={selectMenuItem}
                    showFocus={openedByKeyboard}
                    stagedOrganizationCount={stagedOrganizationCount}
                    messageCount={messageCount}
                    lastReload={lastReload}
                    showReports={reportsEnabled} />
                )
              }
            </ul>
            <span
              ref={setLastTabRef}
              onFocus={handleFocus}
              tabIndex="0" ></span>
          </div>
        </div>
      }
      {visiblePopup && visiblePopup === 'ProfileModal' &&

        <ProfileModal
          hide={hideVisiblePopup}
          isVisible={!!visiblePopup}
        />

      }
    </nav>
  );
}

